"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
  type GridTone,
} from "@/components/tool-kit/cipher-workbench";
import {
  DataTable,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { foldDiacritics, lettersOnly } from "@/lib/tool-kit/ciphers/alphabet";
import {
  type PolybiusKind,
  polybiusCoordinates,
  polybiusDecode,
  polybiusEncode,
  polybiusLetter,
  polybiusSquare,
} from "@/lib/tool-kit/ciphers/polybius";

const STEP_LIMIT = 12;
const SIZE = 5;
const NUMBER_HEADERS = ["1", "2", "3", "4", "5"];
const TAP_HEADERS = ["•", "••", "•••", "••••", "•••••"];
/** The cell each grid shares between two letters, and how it is labelled. */
const SHARED_CELL: Record<PolybiusKind, readonly [string, string]> = {
  polybius: ["I", "I/J"],
  tap: ["C", "C/K"],
};

/** English strings. A translated content file overrides these through `ui`. */
export const POLYBIUS_SQUARE_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  coordinates: "Coordinates",
  taps: "Tap code",
  decodedText: "Decoded text",
  inputPlaceholder: "Type a message, or paste numbers or taps to decode",
  sample: "Light the torches",
  square: "Grid",
  polybius: "Polybius square (I and J share a cell)",
  tap: "Tap code (C and K share a cell)",
  keyword: "Keyword (optional)",
  keywordHint:
    "Mixes the square: the keyword's letters come first, each once, then the rest of the alphabet. J counts as I.",
  tapHint:
    "The prisoners' grid is fixed, so it takes no keyword. K is tapped as C, and I and J keep separate cells.",
  encodeRule:
    "Each letter becomes its row number, then its column number. Letters are separated by a space and words by a slash; punctuation is dropped.",
  tapEncodeRule:
    "Each letter is two groups of taps, written as dots: the row, then the column. Letters are separated by two spaces and words by a slash.",
  decodeRule:
    "Digits are read in pairs, row first. Spaces, dashes and commas are ignored, so 2315 and 23 15 read the same; a slash starts a new word.",
  tapDecodeRule:
    "Each run of dots (. · • or *) is one group of taps, and the groups are read in pairs, row first; a slash starts a new word.",
  skipped: "Skipped — no cell in the grid: {chars}",
  invalid: "Outside the grid, shown as ?: {pairs}",
  unpaired:
    "one: {count} word ended with half a pair, which was ignored.|other: {count} words ended with half a pair, which were ignored.",
  mergedIJ: "I and J share a cell, so every J comes back as I.",
  mergedCK: "C and K share a cell, so every K comes back as C.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  working: "The grid",
  gridLabel: "Polybius square",
  tapGridLabel: "Tap code grid",
  gridHint:
    "Rows are numbered down the side and columns across the top. Letters in your message are highlighted.",
  stepsHeading:
    "one: Letter by letter — the first letter|other: Letter by letter — the first {count} letters",
  letter: "Letter",
  row: "Row",
  column: "Column",
  code: "Code",
  noLetters: "Type some letters to see the working.",
} as const;

type Strings = typeof POLYBIUS_SQUARE_CIPHER_STRINGS;

function Working({
  kind,
  square,
  message,
  strings,
}: {
  kind: PolybiusKind;
  square: string;
  message: string;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const used = new Set(
    [...lettersOnly(message)].map((letter) => polybiusLetter(letter, kind))
  );
  const [shared, sharedLabel] = SHARED_CELL[kind];
  const cells = [...square].map((letter) =>
    letter === shared ? sharedLabel : letter
  );
  const tone = (index: number): GridTone | undefined =>
    used.has(square.charAt(index)) ? "primary" : undefined;
  const steps = [...foldDiacritics(message)]
    .map((char) => ({ char, at: polybiusCoordinates(char, square, kind) }))
    .filter((step) => step.at !== null)
    .slice(0, STEP_LIMIT);
  const headers = kind === "tap" ? TAP_HEADERS : NUMBER_HEADERS;
  return (
    <>
      <div className="space-y-2">
        <CipherGrid
          cells={cells}
          columnHeaders={headers}
          columns={SIZE}
          label={kind === "tap" ? strings.tapGridLabel : strings.gridLabel}
          rowHeaders={headers}
          tone={tone}
        />
        <p className={HINT}>{strings.gridHint}</p>
      </div>
      {steps.length > 0 ? (
        <div className="space-y-2">
          <p className={LABEL}>
            {tPlural(strings.stepsHeading, locale, steps.length, {
              count: fmt(steps.length),
            })}
          </p>
          <DataTable
            head={[strings.letter, strings.row, strings.column, strings.code]}
            rows={steps.map(({ char, at }) => {
              const row = at?.row ?? 0;
              const column = at?.column ?? 0;
              const code =
                kind === "tap"
                  ? `${".".repeat(row)} ${".".repeat(column)}`
                  : `${row}${column}`;
              return [char.toUpperCase(), fmt(row), fmt(column), code];
            })}
          />
        </div>
      ) : (
        <Notice>{strings.noLetters}</Notice>
      )}
    </>
  );
}

function ruleFor(mode: CipherMode, kind: PolybiusKind, strings: Strings) {
  if (mode === "encode") {
    return kind === "tap" ? strings.tapEncodeRule : strings.encodeRule;
  }
  return kind === "tap" ? strings.tapDecodeRule : strings.decodeRule;
}

export default function PolybiusSquareCipherWidget() {
  const strings = useToolUi(POLYBIUS_SQUARE_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [kind, setKind] = useState<PolybiusKind>("polybius");
  const [keyword, setKeyword] = useState("");

  const square = polybiusSquare(keyword, kind);
  const encoded =
    mode === "encode" ? polybiusEncode(input, square, kind) : null;
  const decoded =
    mode === "decode" ? polybiusDecode(input, square, kind) : null;
  const output = encoded?.output ?? decoded?.output ?? "";
  const codeLabel = kind === "tap" ? strings.taps : strings.coordinates;

  const keyControls = (
    <div className="space-y-4">
      <PillGroup
        label={strings.square}
        name="pb-kind"
        onChange={setKind}
        options={[
          { value: "polybius", label: strings.polybius },
          { value: "tap", label: strings.tap },
        ]}
        value={kind}
      />
      {kind === "polybius" ? (
        <TextField
          hint={strings.keywordHint}
          id="pb-keyword"
          label={strings.keyword}
          mono
          onChange={setKeyword}
          value={keyword}
        />
      ) : (
        <p className={HINT}>{strings.tapHint}</p>
      )}
    </div>
  );

  const notices = (
    <>
      <p className={HINT}>{ruleFor(mode, kind, strings)}</p>
      {encoded && encoded.skipped.length > 0 ? (
        <Notice>
          {t(strings.skipped, { chars: encoded.skipped.join(" ") })}
        </Notice>
      ) : null}
      {decoded && decoded.invalid.length > 0 ? (
        <Notice>
          {t(strings.invalid, { pairs: decoded.invalid.join(", ") })}
        </Notice>
      ) : null}
      {decoded && decoded.unpaired > 0 ? (
        <Notice>
          {tPlural(strings.unpaired, locale, decoded.unpaired, {
            count: fmt(decoded.unpaired),
          })}
        </Notice>
      ) : null}
    </>
  );

  return (
    <CipherWorkbench
      idPrefix="pb"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : codeLabel,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? codeLabel : strings.decodedText,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={notices}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      outputNotes={
        decoded && output !== "" ? (
          <p className={HINT}>
            {kind === "tap" ? strings.mergedCK : strings.mergedIJ}
          </p>
        ) : null
      }
      working={
        <Working
          kind={kind}
          message={mode === "encode" ? input : output}
          square={square}
          strings={strings}
        />
      }
    />
  );
}
